import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Stack,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Link,
  Tooltip,
} from "@mui/material";
import ArrowBack from "@mui/icons-material/ArrowBack";
// import LocationOn from "@mui/icons-material/LocationOn";
import AccountBalanceWallet from "@mui/icons-material/AccountBalanceWallet";
import SarIcon from "../icons/SarIcon";
import AccessTime from "@mui/icons-material/AccessTime";
import DonutLarge from "@mui/icons-material/DonutLarge";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import TagOutlined from "@mui/icons-material/TagOutlined";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import NorthEast from "@mui/icons-material/NorthEast";
import noImgPlaceholder from "../../Assets/no-img-placeholder.png";
import { useNavigate, useParams } from "react-router-dom";
import { useGetInvestorAssetSummaryQuery, useLazyGetInvestorAssetByIdQuery } from "../../redux/api/authApi";
import { resolveInvestorUploadUrl } from "./myInvestmentsMapper";
import InvestorInvestModal from "./InvestorInvestModal";

/** API may return absolute URLs or relative paths for images/documents */
function summaryMediaUrl(url) {
  if (!url) return "";
  const s = String(url).trim();
  if (/^https?:\/\//i.test(s)) return s;
  return resolveInvestorUploadUrl(s);
}

const TEAL = "#00bfa5";
const TEAL_DARK = "#0d9488";
const PURPLE = "#7c3aed";
const CARD_SHADOW = "0 4px 32px rgba(15, 23, 42, 0.07)";
const CTA_GRADIENT = `linear-gradient(135deg, ${TEAL} 0%, #009688 55%, ${TEAL_DARK} 100%)`;
const CTA_SHADOW = "0 10px 28px rgba(0, 191, 165, 0.38)";

function formatSar(n) {
  const x = typeof n === "number" ? n : Number(n);
  if (!Number.isFinite(x)) return "—";
  return new Intl.NumberFormat("en-SA", { maximumFractionDigits: 0 }).format(x);
}

function formatTokens(n) {
  const x = typeof n === "number" ? n : Number(n);
  if (!Number.isFinite(x)) return "0";
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(x);
}

function TruncatedCell({ value }) {
  const text = value == null || value === "" ? "-" : String(value);
  if (text === "-" || text.length <= 10) {
    return <span>{text}</span>;
  }
  return (
    <Tooltip title={text} arrow placement="top">
      <span style={{ cursor: "help" }}>{`${text.slice(0, 10)}…`}</span>
    </Tooltip>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString("en-SA", { year: "numeric", month: "short", day: "numeric" });
}

const TABS = [
  { id: "overview", label: "Overview" },
  // { id: "performance", label: "Performance" },
  { id: "transactions", label: "Transactions" },
  { id: "documents", label: "Documents" },
];

export default function InvestorAssetManagedView() {
  const { assetId } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [investOpen, setInvestOpen] = useState(false);
  const [heroImgFailed, setHeroImgFailed] = useState(false);

  // const {
  //   data: summaryRes,
  //   isLoading: sumLoading,
  //   isError: sumError,
  //   error: sumErr,
  //   refetch: refetchSummary,
  // } = useGetInvestorAssetSummaryQuery(assetId, { skip: !assetId });

  // const summaryData = summaryRes?.data?.data ?? summaryRes?.data;
  // const summaryStatusFunded =
  //   String(summaryData?.asset?.status ?? "").trim().toLowerCase() === "funded";

  // const { data: assetRes, refetch: refetchAssetById } = useLazyGetInvestorAssetByIdQuery(assetId, {
  //   skip:
  //     !assetId ||
  //     sumLoading ||
  //     (!sumError && summaryStatusFunded),
  // });

  const {
  data: summaryRes,
  isLoading: sumLoading,
  isFetching: sumFetching,
  isError: sumError,
  error: sumErr,
  refetch: refetchSummary,
} = useGetInvestorAssetSummaryQuery(assetId, {
  skip: !assetId,
});

const summaryData = summaryRes?.data?.data ?? summaryRes?.data;

const mintT = summaryData?.asset?.transactionHash || null;

console.warn("mintTxHash", mintT);

const summaryStatusFunded =
  String(summaryData?.asset?.status ?? "")
    .trim()
    .toLowerCase() === "funded";

const [
  getInvestorAssetById,
  { data: assetRes, isFetching: assetByIdFetching },
] = useLazyGetInvestorAssetByIdQuery();

useEffect(() => {
  if (!assetId) return;

  // Wait until summary API is completed
  if (sumLoading || sumFetching) return;

  // Stop if summary API failed
  if (sumError) return;

  // Wait until summary data is available
  if (!summaryData?.asset) return;

  // Main condition: if Funded, do not hit asset by id API
  if (summaryStatusFunded) return;

  // Only hit this API when status is not Funded
  getInvestorAssetById(assetId);
}, [
  assetId,
  sumLoading,
  sumFetching,
  sumError,
  summaryData,
  summaryStatusFunded,
  getInvestorAssetById,
]);
  const d = summaryData;
  const asset = d?.asset || {};
  const overview = d?.overview || {};
  const invSum = d?.investmentSummary || {};
  const invDetails = d?.investmentDetails || {};
  const transactions = Array.isArray(d?.transactions) ? d.transactions : [];
  const assetDoc = d?.assetDocument || {};
  const withdrawal =
    d?.availableForWithdrawal != null ? d.availableForWithdrawal : invDetails?.availableForWithdrawal;

  const fullAsset = assetRes?.data?.data ?? assetRes?.data;
  const tokenEconomics =
    fullAsset?.tokenEconomics || asset.tokenEconomics || d?.tokenEconomics || {};
  const financial =
    fullAsset?.financialDetails || asset.financialDetails || d?.financialDetails || {};
  const totalSupply =
    Number(tokenEconomics.totalSupply) ||
    Number(asset.totalSupply) ||
    Number(invSum.totalSupply) ||
    0;
  const tokensSold =
    Number(tokenEconomics.tokensSold) ||
    Number(asset.tokensSold) ||
    Number(invSum.tokensSold) ||
    Number(invDetails.tokensSold) ||
    0;
  const availableTokens = Math.max(0, totalSupply - tokensSold);
  const minimumInvestmentSar =
    Number(financial.minimumInvestment) || Number(asset.minimumInvestment) || 500;
  const pricePerToken =
    Number(invDetails.tokenPrice) ||
    Number(tokenEconomics.tokenPriceUSD) ||
    Number(asset.pricePerToken) ||
    0;
  const basicName = fullAsset?.basicInfo?.assetName || asset.assetName || "—";
  const mainImageUrl = summaryMediaUrl(asset.mainImage || "");

  useEffect(() => {
    setHeroImgFailed(false);
  }, [mainImageUrl]);

  const supplyForPct = Number(invSum.totalSupply) || totalSupply;
  const ownedForPct = Number(invSum.tokensOwned) || 0;
  const ownershipDisplay =
    supplyForPct > 0
      ? `${((ownedForPct / supplyForPct) * 100).toFixed(2)}%`
      : Number.isFinite(Number(overview.ownershipPercent))
        ? `${Number(overview.ownershipPercent).toFixed(2)}%`
        : "—";

  if (!assetId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Missing asset.</Alert>
      </Box>
    );
  }

  if (sumLoading || sumFetching) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
        <CircularProgress sx={{ color: TEAL }} />
      </Box>
    );
  }

  if (sumError || !d) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" onClose={() => refetchSummary()}>
          {sumErr?.data?.message || sumErr?.error || "Could not load asset summary."}
        </Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate("/investor/viewinvest", { state: { viewTab: "my" } })} startIcon={<ArrowBack />}>
          Back to My Assets
        </Button>
      </Box>
    );
  }

  const isActive = String(asset.status || "").toLowerCase() === "active";
  const lastPayoutDt = invSum.nextPayoutDate ? new Date(invSum.nextPayoutDate) : null;
  const lastPayoutValid = lastPayoutDt && !Number.isNaN(lastPayoutDt.getTime());
  const payoutLooksUpcoming = lastPayoutValid && lastPayoutDt > new Date();

  return (
    <Box sx={{ minHeight: "100vh", pb: 10 }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate("/investor/viewinvest", { state: { viewTab: "my" } })}
        sx={{ mb: 2.5, textTransform: "none", color: "#64748b", fontWeight: 600, fontSize: 14 }}
      >
        Back to My Assets
      </Button>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: "1px solid #e2e8f0",
          overflow: "hidden",
          bgcolor: "#fff",
          mb: 3,
          boxShadow: CARD_SHADOW,
        }}
      >
        <Grid container sx={{ flexWrap: { xs: "wrap", md: "nowrap" }, alignItems: "stretch" }}>
          <Grid
            size={{ xs: 12, md: 7, lg: 6 }}
            sx={{
              display: "flex",
              flexDirection: "column",
              bgcolor: "#fff",
              p: { xs: 2, sm: 2.5, md: 3 },
              alignSelf: "stretch",
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                position: "relative",
                width: "100%",
                borderRadius: 3,
                overflow: "hidden",
                bgcolor: "#e8eef4",
                border: "1px solid #e2e8f0",
                boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.6)",
              }}
            >
              <Box
                component="img"
                src={heroImgFailed || !mainImageUrl ? noImgPlaceholder : mainImageUrl}
                alt={basicName || "Asset"}
                loading="eager"
                decoding="async"
                onError={() => setHeroImgFailed(true)}
                sx={{
                  width: "100%",
                  height: "auto",
                  display: "block",
                }}
              />
              <Chip
                label={asset.status || "Active"}
                size="small"
                sx={{
                  position: "absolute",
                  top: 14,
                  left: 14,
                  zIndex: 2,
                  height: 28,
                  bgcolor: isActive ? "#22c55e" : "#64748b",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: 12,
                  boxShadow: "0 2px 8px rgba(15,23,42,0.12)",
                  "& .MuiChip-label": { px: 1.25 },
                }}
              />
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 5, lg: 6 }} sx={{ display: "flex", minWidth: 0 }}>
            <Box sx={{ p: { xs: 2.5, sm: 3, md: 4 }, pt: { xs: 2, md: 4 }, flex: 1, display: "flex", flexDirection: "column" }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  color: "#0f172a",
                  fontSize: { xs: 22, sm: 26, md: 28 },
                  lineHeight: 1.2,
                  letterSpacing: "-0.02em",
                  mb: 1.25,
                }}
              >
                {basicName}
              </Typography>
              {/* <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 2 }}>
                <LocationOn sx={{ fontSize: 22, color: "#94a3b8" }} />
                <Typography variant="body1" sx={{ color: "#64748b", fontWeight: 500, fontSize: 15 }}>
                  {asset.location && String(asset.location).trim() !== "-" ? asset.location : "—"}
                </Typography>
              </Stack> */}
              <Stack direction="row" flexWrap="wrap" useFlexGap gap={1} sx={{ mb: 2.5 }}>
                <Chip
                  label={asset.category || "—"}
                  size="small"
                  sx={{
                    bgcolor: "#e0f2fe",
                    color: "#0369a1",
                    fontWeight: 700,
                    borderRadius: 2,
                    height: 30,
                  }}
                />
                {asset.propertyType ? (
                  <Chip
                    label={asset.propertyType}
                    size="small"
                    variant="outlined"
                    sx={{ fontWeight: 700, borderRadius: 2, height: 30, borderColor: "#cbd5e1", color: "#475569" }}
                  />
                ) : null}
              </Stack>

              {asset.description ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2.5, lineHeight: 1.65, display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}
                >
                  {asset.description}
                </Typography>
              ) : null}

              <Grid container spacing={2} sx={{ mb: 3 }}>
                {[
                  {
                    label: "Your Investment",
                    value: `SAR ${formatSar(overview.yourInvestment)}`,
                    sub: null,
                    accent: "#0369a1",
                    accentBg: "#dbeafe",
                    icon: <AccountBalanceWallet sx={{ fontSize: 16 }} />,
                    valueColor: "#0f172a",
                  },
                  {
                    label: "Current Value",
                    value: `SAR ${formatSar(overview.currentValue)}`,
                    sub: null,
                    accent: "#16a34a",
                    accentBg: "#dcfce7",
                    icon: <SarIcon sx={{ fontSize: 16 }} />,
                    valueColor: "#16a34a",
                  },
                  // {
                  //   label: "Total Gain",
                  //   value: `SAR ${formatSar(overview.totalGain)}`,
                  //   sub: formatPct(overview.gainPercent),
                  //   accent: gainPositive ? "#16a34a" : gain < 0 ? "#dc2626" : "#64748b",
                  //   accentBg: gainPositive ? "#dcfce7" : gain < 0 ? "#fee2e2" : "#f1f5f9",
                  //   icon: <TrendingUp sx={{ fontSize: 16 }} />,
                  //   valueColor: gainPositive ? "#16a34a" : gain < 0 ? "#dc2626" : "#0f172a",
                  // },
                  {
                    label: "Ownership",
                    value: ownershipDisplay,
                    sub: "of total supply",
                    accent: PURPLE,
                    accentBg: "#ede9fe",
                    icon: <DonutLarge sx={{ fontSize: 16 }} />,
                    valueColor: "#0f172a",
                  },
                ].map((cell) => (
                  <Grid size={{ xs: 6 }} key={cell.label}>
                    <Box
                      sx={{
                        bgcolor: "#fff",
                        borderRadius: 2.5,
                        p: 2,
                        border: "1px solid #e2e8f0",
                        borderTop: `3px solid ${cell.accent}`,
                        height: "100%",
                        boxShadow: "0 1px 6px rgba(15,23,42,0.06)",
                        transition: "box-shadow 0.2s, transform 0.15s",
                        "&:hover": {
                          boxShadow: "0 6px 20px rgba(15,23,42,0.1)",
                          transform: "translateY(-2px)",
                        },
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: 1,
                            bgcolor: cell.accentBg,
                            color: cell.accent,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          {cell.icon}
                        </Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#64748b",
                            fontWeight: 700,
                            lineHeight: 1.25,
                            letterSpacing: "0.01em",
                            minWidth: 0,
                          }}
                        >
                          {cell.label}
                        </Typography>
                      </Stack>
                      <Typography
                        sx={{
                          fontWeight: 800,
                          fontSize: 15,
                          color: cell.valueColor,
                          lineHeight: 1.3,
                          wordBreak: "break-word",
                        }}
                      >
                        {cell.value}
                      </Typography>
                      {cell.sub && (
                        <Typography
                          variant="caption"
                          sx={{ color: cell.accent, fontWeight: 700, mt: 0.25, display: "block" }}
                        >
                          {cell.sub}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                ))}
              </Grid>

              {(asset?.deployment?.mintTxHash || fullAsset?.deployment?.mintTxHash)  || mintT ? (
                <Box
                  sx={{
                    mb: 1.5,
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: "#f8fafc",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.75 }}>
                    <TagOutlined sx={{ fontSize: 15, color: TEAL_DARK }} />
                    <Typography variant="caption" fontWeight={700} sx={{ color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                      Transaction Hash
                    </Typography>
                  </Stack>
                  <Typography
                    component="a"
                    href={`https://etherscan.io/tx/${asset?.deployment?.mintTxHash || fullAsset?.deployment?.mintTxHash || mintT}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="caption"
                    sx={{
                      display: "block",
                      fontFamily: "monospace",
                      fontWeight: 600,
                      fontSize: 15,
                      color: TEAL_DARK,
                      wordBreak: "break-all",
                      lineHeight: 1.5,
                      textDecoration: "none",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    {asset?.deployment?.mintTxHash || fullAsset?.deployment?.mintTxHash || mintT}
                  </Typography>
                </Box>
              ) : null}

              <Stack direction="row" gap={1} sx={{ mt: "auto", flexWrap: "nowrap" }}>
                <Button
                  disabled={asset.status === 'Funded'}
                  variant="contained"
                  disableElevation
                  endIcon={<NorthEast sx={{ fontSize: 13 }} />}
                  onClick={() => setInvestOpen(true)}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: 2,
                    px: 1.5,
                    py: 0.75,
                    fontSize: 12,
                    whiteSpace: "nowrap",
                    flex: "1 1 0",
                    minWidth: 0,
                    background: CTA_GRADIENT,
                    boxShadow: CTA_SHADOW,
                    "&:hover": {
                      background: CTA_GRADIENT,
                      boxShadow: "0 8px 24px rgba(0, 191, 165, 0.4)",
                      filter: "brightness(1.03)",
                    },
                  }}
                >
                  Invest More
                </Button>
                {/* <Button
                  variant="outlined"
                  endIcon={<SouthWest sx={{ fontSize: 13 }} />}
                  disabled
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: 2,
                    px: 1.5,
                    py: 0.75,
                    fontSize: 12,
                    whiteSpace: "nowrap",
                    flex: "1 1 0",
                    minWidth: 0,
                    borderColor: "#cbd5e1",
                    color: "#64748b",
                  }}
                >
                  Sell Tokens
                </Button>
                <Button
                  variant="outlined"
                  endIcon={<SwapHoriz sx={{ fontSize: 13 }} />}
                  disabled
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: 2,
                    px: 1.5,
                    py: 0.75,
                    fontSize: 12,
                    whiteSpace: "nowrap",
                    flex: "1 1 0",
                    minWidth: 0,
                    borderColor: "#cbd5e1",
                    color: "#64748b",
                  }}
                >
                  Transfer Tokens
                </Button> */}
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 0.5,
          p: 0.6,
          borderRadius: 2,
          border: "1px solid #e2e8f0",
          bgcolor: "#fff",
          mb: 3,
          width: "100%",
          boxShadow: "0 2px 12px rgba(15, 23, 42, 0.04)",
        }}
      >
        {TABS.map((t) => {
          const active = tab === t.id;
          return (
            <Button
              key={t.id}
              onClick={() => setTab(t.id)}
              sx={{
                textTransform: "none",
                fontWeight: active ? 800 : 600,
                color: active ? "#0f172a" : "#64748b",
                bgcolor: active ? "#f1f5f9" : "transparent",
                borderRadius: 999,
                px: 2.5,
                py: 1.1,
                minWidth: 0,
                fontSize: 14,
                "&:hover": { bgcolor: active ? "#e2e8f0" : "rgba(148, 163, 184, 0.12)" },
              }}
            >
              {t.label}
            </Button>
          );
        })}
      </Paper>

      {tab === "overview" && (
        <>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <Paper
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  border: "1px solid #eef2f7",
                  height: "100%",
                  boxShadow: CARD_SHADOW,
                  bgcolor: "#fff",
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      bgcolor: "#e8f8f5",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <AccountBalanceWallet sx={{ color: TEAL_DARK }} />
                  </Box>
                  <Typography fontWeight={800}>Tokens Owned</Typography>
                </Stack>
                <Typography variant="h5" fontWeight={800}>
                  {formatTokens(invSum.tokensOwned)}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  {ownershipDisplay} of total supply
                </Typography>
                <Chip label="Active" size="small" sx={{ mt: 1.5, bgcolor: "#dcfce7", color: "#166534", fontWeight: 700 }} />
              </Paper>
            </Grid>
            {/* <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <Paper
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  border: "1px solid #eef2f7",
                  height: "100%",
                  boxShadow: CARD_SHADOW,
                  bgcolor: "#fff",
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      bgcolor: "#e8f8f5",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <TrendingUp sx={{ color: TEAL_DARK }} />
                  </Box>
                  <Typography fontWeight={800}>Total Returns</Typography>
                </Stack>
                <Typography variant="h5" fontWeight={800}>
                  SAR {formatSar(invSum.totalReturn)}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  Capital appreciation
                </Typography>
                <Typography variant="body2" fontWeight={700} color="#16a34a" sx={{ mt: 1 }}>
                  {formatPct(overview.gainPercent)}
                </Typography>
              </Paper>
            </Grid> */}
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <Paper
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  border: "1px solid #eef2f7",
                  height: "100%",
                  boxShadow: CARD_SHADOW,
                  bgcolor: "#fff",
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      bgcolor: "#e8f8f5",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <SarIcon sx={{ color: TEAL_DARK }} />
                  </Box>
                  <Typography fontWeight={800}>Total Income</Typography>
                </Stack>
                <Typography variant="h5" fontWeight={800}>
                  SAR {formatSar(invSum.totalRevenue)}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  Rental + dividends
                </Typography>
                <Typography variant="body2" fontWeight={700} color="#16a34a" sx={{ mt: 1 }}>
                  {invDetails.rentalYield != null ? `${Number(invDetails.rentalYield).toFixed(1)}%` : "—"}
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <Paper
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  border: "1px solid #eef2f7",
                  height: "100%",
                  boxShadow: CARD_SHADOW,
                  bgcolor: "#fff",
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      bgcolor: "#f5f3ff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <AccessTime sx={{ color: PURPLE }} />
                  </Box>
                  <Typography fontWeight={800}>Next payout</Typography>
                </Stack>
                <Typography variant="h5" fontWeight={800}>
                  SAR {(invSum.nextPayout)}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  {invSum.nextPayoutDate
                    ? formatDate(invSum.nextPayoutDate)
                    : (invSum.nextPayout) > 0
                      ? "—"
                      : "No scheduled date"}
                </Typography>
                <Chip
                  label={
                    payoutLooksUpcoming
                      ? "Upcoming"
                      : invSum.nextPayoutDate || Number(invSum.nextPayoutDate) > 0
                        ? "Recorded"
                        : "None yet"
                  }
                  size="small"
                  sx={{ mt: 1.5, bgcolor: "#ede9fe", color: PURPLE, fontWeight: 700 }}
                />
              </Paper>
            </Grid>
          </Grid>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            {/* Investment Value Growth
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 3, borderRadius: 3, border: "1px solid #eef2f7", height: "100%", boxShadow: CARD_SHADOW, bgcolor: "#fff" }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                  <ShowChart sx={{ color: TEAL_DARK }} />
                  <Typography fontWeight={800}>Investment Value Growth</Typography>
                </Stack>
                <Box sx={{ width: "100%", height: 280 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartGrowth} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id={`managedGrowth-${assetId}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={TEAL} stopOpacity={0.35} />
                          <stop offset="100%" stopColor={TEAL} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} axisLine={{ stroke: "#e2e8f0" }} />
                      <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={{ stroke: "#e2e8f0" }} tickFormatter={(v) => (Number(v) >= 1000 ? `${Math.round(Number(v) / 1000)}k` : String(v))} />
                      <Tooltip formatter={(v) => [`SAR ${formatSar(v)}`, ""]} />
                      <Area type="monotone" dataKey="actualValue" name="Actual" stroke={TEAL} strokeWidth={2.5} fill={`url(#managedGrowth-${assetId})`} />
                      <Area type="monotone" dataKey="targetValue" name="Target" stroke={BLUE} strokeWidth={1.5} fill="none" strokeDasharray="4 4" />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>

            Income Distribution
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 3, borderRadius: 3, border: "1px solid #eef2f7", height: "100%", boxShadow: CARD_SHADOW, bgcolor: "#fff" }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                  <BarChartIcon sx={{ color: TEAL_DARK }} />
                  <Typography fontWeight={800}>Income Distribution</Typography>
                </Stack>
                <Box sx={{ width: "100%", height: 280 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartIncome} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} axisLine={{ stroke: "#e2e8f0" }} />
                      <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={{ stroke: "#e2e8f0" }} />
                      <Tooltip formatter={(v) => [`SAR ${formatSar(v)}`, ""]} />
                      <Bar dataKey="rentalIncome" name="Rental" stackId="a" fill={TEAL} radius={[4, 4, 0, 0]} />
                      <Bar dataKey="capitalGain" name="Capital gain" stackId="a" fill={BLUE} radius={[4, 4, 0, 0]} />
                      <Legend />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
                {Array.isArray(incomeDistribution?.labels) && incomeDistribution.labels.length > 0 ? (
                  <Stack spacing={0.75} sx={{ mt: 2, pt: 2, borderTop: "1px solid #e2e8f0" }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>Income by source (summary)</Typography>
                    {incomeDistribution.labels.map((label, i) => (
                      <Stack key={`${label}-${i}`} direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2">{label}</Typography>
                        <Typography variant="body2" fontWeight={700}>SAR {formatSar(Array.isArray(incomeDistribution.values) ? incomeDistribution.values[i] : 0)}</Typography>
                      </Stack>
                    ))}
                    {incomeDistribution.total != null && Number.isFinite(Number(incomeDistribution.total)) ? (
                      <Typography variant="body2" fontWeight={800} sx={{ pt: 1 }}>Total: SAR {formatSar(incomeDistribution.total)}</Typography>
                    ) : null}
                  </Stack>
                ) : null}
              </Paper>
            </Grid>

            Value Allocation
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 3, borderRadius: 3, border: "1px solid #eef2f7", height: "100%", boxShadow: CARD_SHADOW, bgcolor: "#fff" }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                  <DonutLarge sx={{ color: TEAL_DARK }} />
                  <Typography fontWeight={800}>Value Allocation</Typography>
                </Stack>
                <Box sx={{ width: "100%", height: 280, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {pieData.length === 0 || pieData.every((p) => p.value === 0) ? (
                    <Typography color="text.secondary">No allocation data yet.</Typography>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={pieData} cx="50%" cy="50%" innerRadius={68} outerRadius={96} paddingAngle={2} dataKey="value" nameKey="name">
                          {pieData.map((_, i) => (<Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />))}
                        </Pie>
                        <Tooltip formatter={(v) => `SAR ${formatSar(v)}`} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </Box>
                <Stack spacing={0.75} sx={{ mt: 1 }}>
                  {pieData.map((p, i) => (
                    <Stack key={p.name} direction="row" justifyContent="space-between">
                      <Typography variant="body2" fontWeight={600}>{p.name}</Typography>
                      <Typography variant="body2" fontWeight={800}>SAR {formatSar(p.value)}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            </Grid>
            */}

            <Grid size={{ xs: 12, md: 6 }}>
              <Paper
                sx={{ p: 3, borderRadius: 3, border: "1px solid #eef2f7", height: "100%", boxShadow: CARD_SHADOW, bgcolor: "#fff" }}
              >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                  <InfoOutlined sx={{ color: TEAL_DARK }} />
                  <Typography fontWeight={800}>Investment Details</Typography>
                </Stack>
                <Grid container spacing={2}>
                  {[
                    { label: "Purchase Date", value: formatDate(invDetails.purchaseDate) },
                    { label: "Token Price", value: `SAR ${formatSar(invDetails.tokenPrice)}` },
                    { label: "Expected ROI", value: invDetails.expectedROI != null ? `${invDetails.expectedROI}%` : "—" },
                    { label: "Rental Yield", value: invDetails.rentalYield != null ? `${invDetails.rentalYield}%` : "—" },
                    { label: "Blockchain", value: invDetails.blockchain || "—" },
                    { label: "Token Type", value: invDetails.tokenType || "—" },
                    { label: "Total Profit", value: `SAR ${formatSar(invDetails.totalProfit)}` },
                    { label: "Available for Reinvest", value: `SAR ${formatSar(invDetails.availableForReinvest)}` },
                  ].map((row) => (
                    <Grid size={{ xs: 12, sm: 6 }} key={row.label}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        {row.label}
                      </Typography>
                      <Typography fontWeight={700}>{row.value}</Typography>
                    </Grid>
                  ))}
                </Grid>
                <Box
                  sx={{
                    mt: 2.5,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: "#ecfdf5",
                    border: "1px solid #bbf7d0",
                  }}
                >
                  <Typography variant="body2" color="#14532d">
                    Available for withdrawal:{" "}
                    <Typography component="span" fontWeight={800}>
                      SAR {formatSar(withdrawal)}
                    </Typography>{" "}
                    in accumulated earnings
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}

      {/* Performance tab — commented out
      {tab === "performance" && (
        <Grid container spacing={3}>
          {[
            { title: analytics.vsTargetROI?.label, value: analytics.vsTargetROI?.value, suffix: "%" },
            { title: analytics.annualizedReturn?.label, value: analytics.annualizedReturn?.value, suffix: "%" },
            { title: analytics.sharpeRatio?.label, value: analytics.sharpeRatio?.value != null ? analytics.sharpeRatio.value : "—", suffix: "" },
          ].map((row, i) => (
            <Grid size={{ xs: 12, md: 4 }} key={i}>
              <Paper sx={{ p: 2.5, borderRadius: 3, border: "1px solid #e2e8f0", height: "100%" }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">{row.title || "—"}</Typography>
                <Typography variant="h6" fontWeight={800} sx={{ mt: 1 }}>
                  {row.value != null && row.value !== "" && typeof row.value === "number" ? `${row.value}${row.suffix || ""}` : row.value || "—"}
                </Typography>
              </Paper>
            </Grid>
          ))}
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 3, borderRadius: 3, border: "1px solid #e2e8f0" }}>
              <Typography fontWeight={800} sx={{ mb: 2 }}>Performance trends</Typography>
              <Box sx={{ width: "100%", height: 260 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={(analytics.valueGrowthTrend || chartGrowth).map((r) => ({ ...r, label: formatMonthLabel(r.month), actualValue: Number(r.actualValue) || 0 }))} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => `SAR ${formatSar(v)}`} />
                    <Area type="monotone" dataKey="actualValue" stroke={TEAL} fill={TEAL} fillOpacity={0.2} />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}
      */}

      {tab === "transactions" && (
        <Paper sx={{ borderRadius: 3, border: "1px solid #e2e8f0", overflow: "hidden" }}>
          {transactions.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography color="text.secondary">No transactions yet.</Typography>
            </Box>
          ) : (
            <Table size="small">
              <TableHead sx={{ bgcolor: "#f8fafc" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Tokens</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Amount (SAR)</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Pay</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Market Type</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Transaction ID</TableCell>
                  {/* <TableCell sx={{ fontWeight: 700 }}>Status</TableCell> */}
                  <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.map((tx) => (
                  <TableRow key={tx._id}>
                    <TableCell sx={{ textTransform: "capitalize" }}>{tx.type || "—"}</TableCell>
                    <TableCell>{formatTokens(tx.tokenAmount)}</TableCell>
                    <TableCell>{formatSar(tx.amount)}</TableCell>
                    <TableCell>{tx.paymentMethod || "-"}</TableCell>
                    <TableCell>{tx.marketType || "-"}</TableCell>
                    <TableCell><TruncatedCell value={tx.transactionId} /></TableCell>
                    {/* <TableCell>
                      <Chip
                        label={
                          tx.status ??
                          (String(tx.type || "").toLowerCase() === "buy" ? "Confirmed" : "—")
                        }
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                    </TableCell> */}
                    <TableCell>{formatDate(tx.createdAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      )}

      {tab === "documents" && (
        <Stack spacing={2}>
          {Object.entries(assetDoc).map(([key, doc]) => {
            const title = key
              .replace(/([A-Z])/g, " $1")
              .replace(/^./, (s) => s.toUpperCase())
              .trim();
            const href = doc?.fileUrl ? summaryMediaUrl(doc.fileUrl) : "";
            const fileSize = doc?.fileSize
              ? doc.fileSize >= 1024 * 1024
                ? `${(doc.fileSize / (1024 * 1024)).toFixed(1)} MB`
                : `${Math.round(doc.fileSize / 1024)} KB`
              : null;
            return (
              <Paper key={key} variant="outlined" sx={{ p: 2, borderRadius: 2, display: "flex", alignItems: "center", gap: 2 }}>
                <DescriptionOutlined sx={{ color: "#0284c7", flexShrink: 0 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography fontWeight={700} noWrap>{title}</Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="caption" color="text.secondary">
                      {doc?.fileType ? String(doc.fileType).toUpperCase() : "—"}
                    </Typography>
                    {fileSize && (
                      <Typography variant="caption" color="text.secondary">· {fileSize}</Typography>
                    )}
                  </Stack>
                </Box>
                {href ? (
                  <Link href={href} target="_blank" rel="noopener noreferrer" underline="hover" fontWeight={600} sx={{ flexShrink: 0 }}>
                    Open
                  </Link>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                    Not available
                  </Typography>
                )}
              </Paper>
            );
          })}
        </Stack>
      )}

      <InvestorInvestModal
        open={investOpen}
        onClose={() => setInvestOpen(false)}
        assetId={assetId}
        ownerId={fullAsset?.createdBy?._id || asset?.createdBy?._id}
        assetName={basicName}
        pricePerTokenSar={pricePerToken}
        minimumInvestmentSar={minimumInvestmentSar}
        availableTokens={availableTokens}
        stripeAccountId={
          fullAsset?.stripeAccountId ||
          fullAsset?.createdBy?.stripeAccountId ||
          asset?.stripeAccountId ||
          asset?.createdBy?.stripeAccountId
        }
      />
    </Box>
  );
}
